import { assign, createMachine } from "xstate";
import {
  activatePromoCode,
  addPromoCode,
  fetchPromoCodes,
  IPromoCode,
} from "@nju/data-access-portal-order";

export interface IContext {
  input: string;
  inputError?: string;
  formError?: string;
  promoCodes?: IPromoCode;
  userId: string | undefined;
  subId: string | undefined;
  codeId: string;
  productId: string | undefined;
}

type IEvent =
  | {
      type: "SET_INPUT_VALUE";
      value: string;
    }
  | {
      type: "FETCH_PROMO_CODES_SUCCESS";
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any;
    }
  | {
      type: "FETCH_PROMO_CODES_ERROR";
    }
  | {
      type: "ADD_PROMO_CODE_ERROR";
    }
  | {
      type: "ADD_PROMO_CODE_SUCCESS";
    }
  | {
      type: "ACTIVATE_PROMO_CODE";
      targetUserId: string | undefined;
      subId: string | undefined;
      codeId: string;
    }
  | {
      type: "ACTIVATE_PROMO_CODE_SUCCESS";
    }
  | {
      type: "ACTIVATE_PROMO_CODE_ERROR";
    }
  | { type: "SUBMIT" };

export const promocodesMachine = createMachine(
  {
    context: {
      input: "",
      codeId: "",
      userId: "",
      subId: "",
      productId: "",
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "fetchPromoCodes",
    states: {
      fetchPromoCodes: {
        invoke: {
          src: "fetchPromoCodes",
        },
        on: {
          FETCH_PROMO_CODES_SUCCESS: {
            actions: "setPromoCodes",
            target: "ready",
          },
          FETCH_PROMO_CODES_ERROR: {
            target: "ready",
          },
        },
      },
      addPromoCode: {
        invoke: {
          src: "addPromoCode",
        },
        on: {
          ADD_PROMO_CODE_SUCCESS: {
            actions: ["clearInput", "clearFormError"],
            target: "fetchPromoCodes",
          },
          ADD_PROMO_CODE_ERROR: {
            actions: "setInputError",
            target: "ready",
          },
        },
      },
      activatePromoCode: {
        invoke: {
          src: "activatePromoCode",
        },
        on: {
          ACTIVATE_PROMO_CODE_SUCCESS: {
            actions: "clearFormError",
            target: "fetchPromoCodes",
          },
          ACTIVATE_PROMO_CODE_ERROR: {
            actions: "setFormError",
            target: "ready",
          },
        },
      },
      ready: {
        on: {
          SET_INPUT_VALUE: {
            actions: "setInputValue",
          },
          SUBMIT: {
            actions: "clearInputError",
            target: "validate",
          },
          ACTIVATE_PROMO_CODE: {
            actions: ["setItemForActivation", "clearFormError"],
            target: "activatePromoCode",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasNoInputErrors",
            target: "addPromoCode",
          },
          {
            target: "ready",
          },
        ],
      },
    },
  },
  {
    services: {
      fetchPromoCodes: ({ userId }) => {
        return async (callback) => {
          const result = await fetchPromoCodes({ userId: userId as string });

          if (result.isError) {
            return callback({ type: "FETCH_PROMO_CODES_ERROR" });
          }

          return callback({
            type: "FETCH_PROMO_CODES_SUCCESS",
            value: result.value,
          });
        };
      },
      addPromoCode: ({ input, productId, userId }) => {
        return async (callback) => {
          const result = await addPromoCode({
            code: input,
            productId: productId as string,
            userId: userId as string,
          });

          if (result.isError) {
            return callback({ type: "ADD_PROMO_CODE_ERROR" });
          }

          return callback({
            type: "ADD_PROMO_CODE_SUCCESS",
          });
        };
      },
      activatePromoCode: ({ userId, codeId, subId }) => {
        return async (callback) => {
          const result = await activatePromoCode({
            targetUserId: userId,
            codeId,
            subId,
          });

          if (result.isError) {
            return callback({ type: "ACTIVATE_PROMO_CODE_ERROR" });
          }

          return callback({
            type: "ACTIVATE_PROMO_CODE_SUCCESS",
          });
        };
      },
    },
    guards: {
      hasNoInputErrors: ({ inputError }) => {
        return inputError === undefined;
      },
    },
    actions: {
      clearInputError: assign<IContext, IEvent>(() => {
        return {
          inputError: undefined,
        };
      }),
      clearInput: assign<IContext, IEvent>(() => {
        return {
          input: "",
        };
      }),
      setInputError: assign<IContext, IEvent>(() => {
        return {
          inputError: "Coś tu nie gra. Popraw kod lub wpisz inny.",
        };
      }),
      setFormError: assign<IContext, IEvent>(() => {
        return {
          formError:
            "Oj, coś się nie udało. To nie Ty, to my. Spróbuj ponownie.",
        };
      }),
      clearFormError: assign<IContext, IEvent>(() => {
        return {
          formError: undefined,
        };
      }),
      validateForm: assign<IContext, IEvent>({
        inputError: ({ input }) => {
          if (input.trim() === "") {
            return "Wpisz kod promocyjny.";
          }

          return;
        },
      }),
      setInputValue: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_INPUT_VALUE") {
          return {
            input: event.value,
            inputError: undefined,
          };
        }

        return context;
      }),
      setPromoCodes: assign<IContext, IEvent>((context, event) => {
        if (event.type === "FETCH_PROMO_CODES_SUCCESS") {
          return {
            promoCodes: event.value,
          };
        }

        return context;
      }),
      setItemForActivation: assign<IContext, IEvent>((context, event) => {
        if (event.type === "ACTIVATE_PROMO_CODE") {
          return {
            subId: event.subId,
            targetUserId: event.targetUserId,
            codeId: event.codeId,
          };
        }

        return context;
      }),
    },
  },
);
